import { CartViewFooter } from "../../cpq/overrides/CartViewFooter/CartViewFooter";
import { CartViewHeading } from "../../cpq/overrides/CartViewHeading/CartViewHeading";
import { useUiTranslator } from "../../hooks/useUiTranslator";
import { useOnboardingStatus } from "../../onboarding/useOnboardingStatus";
import { useAdministrationArea } from "../../providers/administrationAreaProvider";
import { useAppSettings } from "../../providers/appSettingsProvider";
import { useEasySession } from "../../providers/easySessionProvider";
import { CustomLineItems } from "../CustomLineItems/CustomLineItems";

import { JSX, useEffect, useState } from "react";
import ReactGA from "react-ga4";

import { useGetByIdQuotes, useGetByIdQuotesKey } from "@4pace-cpq/core/api";
import { Box, Container } from "@mui/material";
import { useNavigate, useParams } from "@tanstack/react-router";

export type ErrorType = {
  applicationProductId: string;
  exceptionMessage: string;
};

export function CustomCartIdRouteComponent(): JSX.Element | null {
  const { quoteId } = useParams({ strict: false });
  const navigate = useNavigate();
  const t = useUiTranslator();
  const { isLoggedIntoEasy } = useEasySession();
  const { doInitialRedirect } = useAppSettings();
  const { administrationArea } = useAdministrationArea();
  const onboardingStatus = useOnboardingStatus();

  const {
    data: item,
    isLoading,
    isFetching,
  } = useGetByIdQuotes(
    {
      path: { id: quoteId },
      // includes are set on the backend
    },
    [useGetByIdQuotesKey],
  );
  const [errorList, setErrorList] = useState<ErrorType[]>();

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });
    window.scrollTo(0, 0);
  }, []);

  if (!item && !isLoading) {
    navigate({
      to: "/",
    });
  }
  const firstItemGroup = item?.itemGroups?.[0];

  const debouncedNavigation = (): void => {
    setTimeout(() => {
      if (
        doInitialRedirect &&
        !isFetching &&
        firstItemGroup &&
        !firstItemGroup.lineItems &&
        onboardingStatus === "ready" &&
        !isLoggedIntoEasy
      ) {
        navigate({
          to: "/configurator",
          search: () => ({
            itemGroupId: firstItemGroup.id,
            administrationArea: administrationArea,
          }),
        });
      }
    }, 500);
  };

  if (isLoading || !item || !firstItemGroup) {
    return null;
  }
  debouncedNavigation();

  const lineItems = firstItemGroup?.lineItems;
  let allConfigsComplete = false;
  let configurationsIncomplete = 0;
  lineItems?.forEach((lineItem) => {
    if (!lineItem.isConfigurationComplete) {
      configurationsIncomplete++;
    }
  });
  if (configurationsIncomplete === 0) {
    allConfigsComplete = true;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      flexGrow="1"
    >
      <Container>
        <CartViewHeading
          itemGroupId={firstItemGroup.id}
          administrationArea={administrationArea}
        />

        {lineItems?.length ? (
          <CustomLineItems
            customerId={item.customerId}
            quoteId={item.id}
            lineItems={lineItems}
            itemGroupId={firstItemGroup.id}
            errorList={errorList}
          />
        ) : (
          <Box p={4}>
            <span>
              {t(
                "CustomApp:ProjectListEmptyMsg",
                "Your project list is empty. Create a configuration or import a GPS ID to get started.",
              )}
            </span>
          </Box>
        )}
      </Container>
      <CartViewFooter
        lineItems={lineItems}
        setErrorList={setErrorList}
        allConfigsComplete={allConfigsComplete}
      />
    </Box>
  );
}
